import { useState, useCallback, useRef, useEffect } from 'react';
import { MapPin, Navigation, Loader2, X, Check, Search } from 'lucide-react';
import { locationManager } from '../lib/locationManager';
import { searchAddress, reverseGeocode, type GeocodeResult } from '../lib/utils/geocoding';

interface LocationPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  onChange: (latitude: number | null, longitude: number | null, address?: string, countryCode?: string) => void;
  label?: string;
  helperText?: string;
}

function extractCountryCode(result: GeocodeResult): string | undefined {
  if (result.address?.country_code) {
    return result.address.country_code.toLowerCase();
  }
  return undefined;
}

export function LocationPicker({
  latitude,
  longitude,
  onChange,
  label = 'Location',
  helperText = 'Add your location to appear in "Near Me" searches'
}: LocationPickerProps) {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const hasLocation = latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLocation = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    setError(null);

    try {
      const data = await searchAddress(query);
      setSearchResults(data);
      setShowResults(data.length > 0);
    } catch {
      setError('Failed to search location. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    // Debounce the search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 500);
  }, [searchLocation]);

  const selectSearchResult = useCallback((result: GeocodeResult) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    const countryCode = extractCountryCode(result);
    onChange(lat, lon, result.display_name, countryCode);
    setSelectedAddress(result.display_name);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setError(null);
  }, [onChange]);

  const getCurrentLocation = useCallback(() => {
    setLoading(true);
    setError(null);

    locationManager.getLocation({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    })
      .then(async (coords) => {
        const lat = coords.latitude;
        const lon = coords.longitude;

        try {
          const data = await reverseGeocode(lat, lon);
          if (data) {
            const countryCode = data.address?.country_code?.toLowerCase();
            const address = data.display_name || 'Current location';
            onChange(lat, lon, address, countryCode);
            setSelectedAddress(address);
            setLoading(false);
            return;
          }
        } catch {
          // Fall back to no address
        }

        onChange(lat, lon, 'Current location');
        setSelectedAddress('Current location');
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setError(error.message || 'Failed to get location. Please try again.');
      });
  }, [onChange]);

  const clearLocation = useCallback(() => {
    onChange(null, null);
    setSelectedAddress(null);
    setError(null);
  }, [onChange]);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-content">
        {label}
      </label>

      {hasLocation ? (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-green-800 block truncate">
              {selectedAddress || `Location set (${latitude?.toFixed(4)}, ${longitude?.toFixed(4)})`}
            </span>
            <span className="text-xs text-green-600">
              {latitude?.toFixed(4)}, {longitude?.toFixed(4)}
            </span>
          </div>
          <button
            type="button"
            onClick={clearLocation}
            className="ml-auto p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Search Input */}
          <div className="relative" ref={resultsRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                placeholder="Search for an address or city..."
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-content/40" />
              )}
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.place_id}
                    type="button"
                    onClick={() => selectSearchResult(result)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-start gap-2"
                  >
                    <MapPin className="h-4 w-4 text-content/40 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-content line-clamp-2">{result.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Or divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-content/50">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Current Location Button */}
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-gray-300 rounded-lg text-content/70 hover:bg-gray-50 hover:border-accent-text/50 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Getting location...</span>
              </>
            ) : (
              <>
                <Navigation className="h-4 w-4" />
                <span>Use My Current Location</span>
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <MapPin className="h-4 w-4" />
          {error}
        </p>
      )}

      {!error && !hasLocation && (
        <p className="text-xs text-content/50">
          {helperText}
        </p>
      )}
    </div>
  );
}
